import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { AudienceListItemDonut } from '@/components/audiences/audience-list-item-donut';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import type { AudienceItem } from '@/components/audiences/audience-list-types';

interface AudienceListItemProps {
  audience: AudienceItem;
  tenantSlug: string;
  locale: Locales;
}

function formatAudiencePercent(value: number, locale: Locales) {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

function MetricCount({ count, label }: { count: number; label: string }) {
  return (
    <span>
      <span className="text-primary tabular-nums">{count}</span> {label}
    </span>
  );
}

function MetricValue({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <span className="text-primary tabular-nums">{value}</span> {label}
    </span>
  );
}

export function AudienceListItem({
  audience,
  tenantSlug,
  locale,
}: AudienceListItemProps) {
  const { LL } = useI18nContext();

  return (
    <Item variant="outline" asChild>
      <Link to={`/${tenantSlug}/audiences/${audience.id}`}>
        <ItemMedia className="!translate-y-0">
          <AudienceListItemDonut
            targetUsersCount={audience.targetUsersCount}
            totalUserbaseCount={audience.totalUserbaseCount}
            locale={locale}
          />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>{audience.name}</ItemTitle>
          <div
            data-slot="item-description"
            className="text-muted-foreground grid w-fit grid-cols-[auto_auto] gap-x-4 gap-y-1 text-sm font-normal leading-normal"
          >
            <MetricCount
              count={audience.sentCampaignsCount}
              label={LL.audiences.listMetrics.campaignsSent()}
            />
            <MetricValue
              label={LL.audiences.listMetrics.averageCtr()}
              value={formatAudiencePercent(audience.averageCtr, locale)}
            />
            <MetricCount
              count={audience.scheduledCampaignsCount}
              label={LL.audiences.listMetrics.campaignsScheduled()}
            />
            <MetricValue
              label={LL.audiences.listMetrics.averageConversion()}
              value={formatAudiencePercent(
                audience.averageConversionRate,
                locale
              )}
            />
          </div>
        </ItemContent>
        <ItemActions>
          <ChevronRight className="text-primary" />
        </ItemActions>
      </Link>
    </Item>
  );
}
